"use client";

import TextField from "../ui/textField";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { cn } from "@/lib";
import CustomButton from "../ui/customButton";
import { useRouter, useSearchParams } from "next/navigation";
import { useChangePasswordMutation, useLoginMutation } from "@/hooks";
import ErrorMessage from "../ui/errorMessage";
import { useAppStore } from "@/store/general";
import { routes, staticQueryParams } from "@/routes";
import { useTranslations } from "next-intl";
import { FlexBetween } from "../ui";
import { useAuthStore, useModalStore } from "@/store";
import {
  TChangePassword,
  TChangePasswordPayload,
  TLoginPayload,
} from "@/interfaces";
import { changePasswordSchema } from "@/validations";
import { useToastStore } from "@/store/toastStore";

const ChangePasswordForm = ({
  isPage = false,
  backFormStep,
  nextFormStep,
}: {
  isPage?: boolean;
  backFormStep?: () => void;
  nextFormStep?: () => void;
}) => {
  const { token } = useAuthStore();
  const t = useTranslations("auth");
  const searchParams = useSearchParams();
  const { phoneNumber } = useAppStore();
  const router = useRouter();
  const { closeModal } = useModalStore();
  const { setOpenToast } = useToastStore();
  const {
    isPending: isLogging,
    mutate: loginFn,
    error: loginError,
  } = useLoginMutation();
  const {
    isPending: isSending,
    mutate: changePasswordFn,
    error: changePasswordError,
  } = useChangePasswordMutation(token!);
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<TChangePassword>({
    resolver: zodResolver(changePasswordSchema),
  });
  const onSubmit = (data: TChangePassword) => {
    const payload: Readonly<TChangePasswordPayload> = {
      // old_password: data.oldPassword,
      new_password: data.newPassword,
      new_password_confirmation: data.newPassword,
    };

    changePasswordFn(payload, {
      onSuccess: () => {
        const loginPayload: TLoginPayload = {
          mobile: phoneNumber,
          password: data.newPassword,
        };
        loginFn(loginPayload, {
          onSuccess: () => {
            if (isPage) {
              const redirectPath =
                searchParams?.get(staticQueryParams.redirect) ||
                routes.main.HOME.primaryPath;
              router.push(redirectPath);
              return;
            }
            closeModal();
            setOpenToast({
              message: "رمز عبور با موفقیت تغییر کرد",
              variant: "success",
            });
            window.location.reload();
          },
        });
      },
    });
  };
  const handleBackStep = () => {
    if (isPage) {
      router.back();
      return;
    }
    if (backFormStep) {
      backFormStep();
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={cn("mb-4 w-full")}>
      {/*<fieldset>*/}
      {/*  <TextField*/}
      {/*    {...register("oldPassword")}*/}
      {/*    hasShadow*/}
      {/*    placeholder={t("placeholders.oldPassword")}*/}
      {/*    type="password"*/}
      {/*    aria-label="password"*/}
      {/*    className="mb-2"*/}
      {/*    inputClassName="shadow-main h-10"*/}
      {/*    value={watch("oldPassword")}*/}
      {/*    error={errors.oldPassword?.message}*/}
      {/*  />*/}
      {/*</fieldset>*/}
      <fieldset>
        <TextField
          {...register("newPassword")}
          hasShadow
          placeholder={t("placeholders.newPassword")}
          type="password"
          aria-label="password"
          className="mb-2"
          inputClassName="h-10"
          value={watch("newPassword")}
          error={errors.newPassword?.message}
        />
      </fieldset>
      {(changePasswordError || loginError) && (
        <ErrorMessage
          message={changePasswordError?.message || loginError?.message}
        />
      )}
      <FlexBetween gap={4} className="mx-auto w-max">
        <CustomButton
          id="change-password-back-step"
          variant={"gray"}
          className="mt-8 w-max px-8!"
          onClick={handleBackStep}
          disabled={isSending || isLogging}
        >
          بازگشت
        </CustomButton>

        <CustomButton
          id="change-password-submit"
          type="submit"
          className="mt-8 w-max"
          isLoading={isSending || isLogging}
        >
          {t("buttons.changePasswordSubmit")}
        </CustomButton>
      </FlexBetween>
    </form>
  );
};

ChangePasswordForm.Skeleton = function Skeleton() {
  return (
    <form className={cn("mb-4 w-full")}>
      <fieldset>
        <div
          className={cn("mb-4 h-10 w-full animate-pulse rounded bg-gray-200")}
        ></div>
      </fieldset>
      <fieldset>
        <div
          className={cn("h-10 w-full animate-pulse rounded bg-gray-200")}
        ></div>
      </fieldset>
      <div className={cn("mt-12 w-max")}>
        <div
          className={cn("h-10 w-24 animate-pulse rounded bg-gray-200")}
        ></div>
      </div>
    </form>
  );
};

export default ChangePasswordForm;
